import i18next from 'i18next'
import round from 'lodash/round'

import {
  GraphFilters,
  GraphFilters_DeviceTypeEnum,
  GraphFilters_GranularityEnum,
  GraphFilters_PropulsionTypeEnum,
  GraphFilters_LocationTypeEnum,
} from 'src/api/gen'
import { GraphFilters_CloseMeansOfTransportEnum, GraphFilters_TripPurposeEnum, Provider } from 'src/api/gen/api'
import { Permission, userHasPermissions } from 'src/commons/permissions'
import { IFilters, GranularityEnum } from 'src/commons/types'
import { ICardTabEntry } from 'src/components/Card/CardTabHeader'
import RestrictedRoute from 'src/layout/RestrictedRoute'
import { SECONDS_IN_MINUTE } from 'src/pages/Trips/constants'

import { TemporalOrNow, ZonedDateTimeToJSON } from './temporalHelpers'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/*
 * The first part of the format is the desired format,
 * and the second part is the used in our filters
 */
export const getDateFormat = (granularity: string) => {
  switch (granularity) {
    case 'month':
      return "yyyy-MM||yyyy-MM-dd'T'HH:mm:ssXXX"
    case 'week':
      return "yyyy-MM-dd||yyyy-MM-dd'T'HH:mm:ssXXX"
    case 'day':
      return "yyyy-MM-dd||yyyy-MM-dd'T'HH:mm:ssXXX"
    case 'hour':
      return "yyyy-MM-dd'T'HH:mm:ssXXX"
    default:
      return "yyyy-MM-dd||yyyy-MM-dd'T'HH:mm:ssXXX"
  }
}

// Tell dayjs which format is the incoming date string
export const getDayJSFormat = (granularity: GranularityEnum): string => {
  switch (granularity) {
    case 'month':
      return 'YYYY-MM'
    case 'week':
      return 'YYYY-MM-DD'
    case 'day':
      return 'YYYY-MM-DD'
    case 'hour':
      return 'YYYY-MM-DD HH:mm'
    default:
      return 'YYYY-MM-DD'
  }
}

export interface IBaseBuildOptions {
  displayLegend?: boolean
  formatWithUnit?: (x: string) => string
  legendPosition?: string
  showTitle?: boolean
  title?: string
  xTicksFormatter?: (value: string) => string
  yTicksFormatter?: (value: any) => string
}
type IMap = { [key in GranularityEnum]: number }

const MAP_GRANULARITY_TO_MS: IMap = {
  hour: HOUR,
  day: DAY,
  week: 7 * DAY,
  month: 30 * DAY,
  quarter: 90 * DAY,
}

function mapGranularityToMS(granularity: GranularityEnum): number {
  // this could and should be handled within ES with a granularity aggregation.
  return MAP_GRANULARITY_TO_MS[granularity]
}

export const findBucketByKey = (buckets: { key: string }[], key: string): any =>
  buckets.find((b: { key: string }) => b.key === key)

export const getDocCountSum = (buckets: { doc_count: number }[]): number =>
  buckets.reduce((a: number, i: { doc_count: number }) => (a += isNaN(i.doc_count) ? 0 : i.doc_count), 0)

export const mapResultValueByGranularity =
  (granularity: GranularityEnum) =>
  (bucket: { key: any; result: { value: number } }): { doc_count: number; key: any } => ({
    key: bucket.key,
    doc_count: round(bucket.result.value / mapGranularityToMS(granularity)),
  })

export function formatNumber(num: number, locale?: string): string {
  return num.toLocaleString(locale || i18next.language) // For now
}

export function buildFilter(
  fieldName: string,
  value: any | any[],
  otherFilters: Record<string, unknown>[]
): Record<string, unknown>[] {
  const filter = [valueMatch(fieldName, value)]
  return [...filter, ...(otherFilters || [])]
}

function valueMatch(fieldName: string, value: any | any[]): Record<string, unknown> {
  const queryType = Array.isArray(value) ? 'terms' : 'match'
  return { [queryType]: { [fieldName]: value } }
}

function getDateFilter(start: string | undefined, end: string | undefined, field = 'start') {
  if (!start && !end) {
    return {}
  }
  return {
    must: [
      {
        range: {
          [field]: {
            ...(start ? { gte: start } : {}),
            ...(end ? { lte: end } : {}),
          },
        },
      },
    ],
  }
}

interface IFilteredQuery {
  bool: {
    [key: string]: any
  }
}

export function filterQuery(
  start: string | undefined,
  end: string | undefined,
  filter: Record<string, unknown>[],
  field = 'start'
): IFilteredQuery {
  const { must = {} } = getDateFilter(start, end, field)
  return {
    bool: {
      must,
      filter,
    },
  }
}

export function commonFilters(filters: IFilters, zonesField: string | null): Record<string, unknown>[] {
  const filterClauses = []

  if (filters.layer && zonesField) {
    filterClauses.push({ exists: { field: `${zonesField}.${filters.layer}` } })
  }

  if (filters.propulsions && filters.propulsions.length !== 0) {
    filterClauses.push(valueMatch('propulsion_type', filters.propulsions))
  }

  if (filters.vehicleTypes && filters.vehicleTypes.length !== 0) {
    filterClauses.push(valueMatch('vehicle_type', filters.vehicleTypes))
  }

  if (filters.providers && filters.providers.length !== 0) {
    filterClauses.push(valueMatch('provider_id', filters.providers))
  }

  return filterClauses
}

export const getDocCount = (f: any) => f.doc_count
export const getDocCountWithoutPercentage = (f: any) => f.doc_count_without_percentage
export const getResultValue = (f: any) => f.result.value

export const getTotalDuration = (bucket: {
  total_duration_per_status: { buckets: { result: { value: number } }[] }
}): { totalDurationDeployed: any[] } => ({
  totalDurationDeployed: bucket.total_duration_per_status.buckets,
})

export const getUniqueCategories = (buckets: { key: any }[][]): any[] =>
  [
    ...new Set(
      buckets
        .map((bucketList: { key: any }[]) => bucketList.map((b: { key: any }) => b.key))
        .reduce((prev, acc) => [...acc, ...prev], [])
    ),
  ].sort()

export const mapProviderIdToName = (providers: Provider[], providerId: string) => {
  const provider = providers.find((prov: Provider) => prov.id === providerId)
  return provider ? provider.name : 'unknown'
}

export function createPayloadFromFilters(filters: IFilters): GraphFilters {
  const data: GraphFilters = {
    time_range_from: filters.range.from ? ZonedDateTimeToJSON(filters.range.from) : undefined,
    time_range_to: filters.range.to ? ZonedDateTimeToJSON(TemporalOrNow(filters.range.to)) : undefined,
    time_range_end_from: filters.endRange?.from ? ZonedDateTimeToJSON(filters.endRange.from) : undefined,
    time_range_end_to: filters.endRange?.to ? ZonedDateTimeToJSON(TemporalOrNow(filters.endRange.to)) : undefined,
    granularity: filters.granularity as unknown as GraphFilters_GranularityEnum,
  }
  if (!!filters.layer) {
    data.layer = filters.layer
  }
  if (!!filters.vehicleTypes) {
    data.device_type = filters.vehicleTypes as unknown as GraphFilters_DeviceTypeEnum[]
  }
  if (!!filters.propulsions) {
    data.propulsion_type = filters.propulsions as unknown as GraphFilters_PropulsionTypeEnum[]
  }
  if (!!filters.providers) {
    data.provider_ids = filters.providers
  }
  if (!!filters.trip_purpose) {
    data.trip_purpose = filters.trip_purpose as unknown as GraphFilters_TripPurposeEnum[]
  }
  if (!!filters.limit) {
    data.limit = filters.limit
  }
  if (!!filters.locationTypes) {
    data.location_type = filters.locationTypes as unknown as GraphFilters_LocationTypeEnum[]
  }
  if (!!filters.duration) {
    {
      data.duration_min = filters.duration[0] * SECONDS_IN_MINUTE
      data.duration_max = filters.duration[1] * SECONDS_IN_MINUTE
    }
  }
  if (!!filters.distance) {
    {
      data.distance_min = filters.distance[0]
      data.distance_max = filters.distance[1]
    }
  }
  if (!!filters.speed) {
    {
      data.speed_min = filters.speed[0]
      data.speed_max = filters.speed[1]
    }
  }

  if (!!filters.day_of_week) {
    data.day_of_week = filters.day_of_week.map((day) => parseInt(day, 10))
  }
  if (!!filters.closeMeansOfTransport) {
    data.close_means_of_transport = filters.closeMeansOfTransport as unknown as GraphFilters_CloseMeansOfTransportEnum[]
  }

  if (!!filters.include_abnormals) {
    data.include_abnormals = filters.include_abnormals
  }

  return data
}

export function isProvidersSet(filters: IFilters | undefined) {
  return filters && filters.providers && filters.providers.length
}

export function formatWeight(weight: string, isMetric = true) {
  if (isMetric) {
    return weight + ' kg'
  }
  return weight + ' lbs'
}

export const makeCancelable = function <T>(promise: Promise<T>): { cancel: () => void; current: Promise<T> } {
  let canceled = false

  const wrappedPromise = new Promise<T>((resolve, reject) => {
    promise.then(
      (val) => (canceled ? reject({ canceled: true }) : resolve(val)),
      (error) => (canceled ? reject({ canceled: true }) : reject(error))
    )
  })

  return {
    current: wrappedPromise,
    cancel() {
      canceled = true
    },
  }
}

interface IRoute extends React.ComponentProps<typeof RestrictedRoute> {
  label?: string
  path: string
  requiredPermissions: Permission[]
}

interface IRouteConfiguration extends IRoute {
  condition?: boolean | (() => boolean)
}

/**
 * @param label Undefined label means "Do not display tab" which is different from
 *              an empty string which means a tab with an empty label
 */
export const useAccessibleRoutes = (routesConfiguration: IRouteConfiguration[]): [ICardTabEntry[], IRoute[]] => {
  const routes: IRoute[] = routesConfiguration
    .filter((route) => {
      switch (typeof route.condition) {
        case 'function':
          return route.condition()
        case 'boolean':
          return route.condition
      }

      return true
    })
    .map((route) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { condition, ...rest } = route // Removes the condition property
      return rest
    })

  const tabs: ICardTabEntry[] = routes
    // Only tabs are filterd on permissions (and not routes) because the RestrictedRoute component handle the redirec
    .filter((route) => route.label !== undefined && userHasPermissions(route.requiredPermissions))
    .map((route, index) => ({ id: index, label: route.label!, to: route.path }))

  return [tabs, routes]
}
